import { z } from 'zod';
export const assetsRegistryCapability = 'assets.registry.v1';
export const assetsFleetCapability = 'assets.fleet.v1';
export const assetsAssetIdSchema = z.string().uuid();
export const assetsCommandIdSchema = z.string().uuid();
export const assetsAssetKindSchema = z.enum([
    'air-mover', 'dehumidifier', 'air-scrubber', 'van', 'trailer', 'tool', 'other',
]);
export const assetsAssetStatusSchema = z.enum([
    'available', 'deployed', 'maintenance', 'retired',
]);
export const assetsFuelTypeSchema = z.enum(['gas', 'diesel', 'electric', 'hybrid']);
const nullableText = (maximum) => z.string().trim().max(maximum).nullable();
const dateSchema = z.string().regex(/^\d{4}-\d{2}-\d{2}$/).refine((value) => {
    const [year, month, day] = value.split('-').map(Number);
    const date = new Date(Date.UTC(year ?? 0, (month ?? 0) - 1, day ?? 0));
    return date.getUTCFullYear() === year && date.getUTCMonth() + 1 === month && date.getUTCDate() === day;
}, 'Date must be a real calendar date in YYYY-MM-DD form.');
export const assetsFleetProfileSchema = z.strictObject({
    vin: nullableText(40),
    licensePlate: nullableText(40),
    year: z.number().int().min(1900).max(2200).nullable(),
    color: nullableText(80),
    fuelType: assetsFuelTypeSchema.nullable(),
    insuranceExpiry: dateSchema.nullable(),
    registrationExpiry: dateSchema.nullable(),
    nextServiceDate: dateSchema.nullable(),
    lastOilChangeDate: dateSchema.nullable(),
    lastOilChangeMileage: z.number().int().nonnegative().nullable(),
    oilChangeIntervalMiles: z.number().int().min(500).max(50_000),
    oilChangeWarningMiles: z.number().int().min(0).max(10_000),
    oilChangeIntervalMonths: z.number().int().min(1).max(60),
});
export const assetsAssetInputSchema = z.strictObject({
    assetTag: z.string().trim().min(1).max(80),
    name: z.string().trim().min(1).max(200),
    kind: assetsAssetKindSchema,
    status: assetsAssetStatusSchema,
    serialNumber: nullableText(160),
    manufacturer: nullableText(160),
    model: nullableText(160),
    purchaseDate: dateSchema.nullable(),
    locationLabel: nullableText(240),
    notes: nullableText(8_000),
    fleet: assetsFleetProfileSchema.nullable(),
}).superRefine((value, context) => {
    const isFleet = value.kind === 'van' || value.kind === 'trailer';
    if (isFleet !== (value.fleet !== null)) {
        context.addIssue({
            code: 'custom',
            path: ['fleet'],
            message: isFleet ? 'Fleet details are required for vans and trailers.' : 'Only vans and trailers may carry fleet details.',
        });
    }
});
export const assetsAssetSchema = z.strictObject({
    tenantId: z.string().uuid(),
    id: assetsAssetIdSchema,
    asset: assetsAssetInputSchema,
    currentMileage: z.number().int().nonnegative().nullable(),
    mileageUpdatedAt: z.string().datetime({ offset: true }).nullable(),
    revision: z.number().int().positive(),
    archivedAt: z.string().datetime({ offset: true }).nullable(),
    createdByMembershipId: z.string().uuid(),
    updatedByMembershipId: z.string().uuid(),
    createdAt: z.string().datetime({ offset: true }),
    updatedAt: z.string().datetime({ offset: true }),
});
export const assetsAssetVersionSchema = z.strictObject({
    id: z.string().uuid(),
    tenantId: z.string().uuid(),
    assetId: assetsAssetIdSchema,
    revision: z.number().int().positive(),
    operation: z.enum(['create', 'update', 'archive', 'restore', 'oil-change']),
    asset: assetsAssetInputSchema,
    currentMileage: z.number().int().nonnegative().nullable(),
    mileageUpdatedAt: z.string().datetime({ offset: true }).nullable(),
    archivedAfter: z.boolean(),
    reason: z.string().trim().min(10).max(500),
    actorMembershipId: z.string().uuid(),
    createdAt: z.string().datetime({ offset: true }),
});
export const assetsCreateAssetCommandSchema = z.strictObject({
    kind: z.literal('create'),
    commandId: assetsCommandIdSchema,
    assetId: assetsAssetIdSchema,
    asset: assetsAssetInputSchema,
    initialMileage: z.number().int().nonnegative().nullable(),
    reason: z.string().trim().min(10).max(500),
});
export const assetsUpdateAssetCommandSchema = z.strictObject({
    kind: z.literal('update'),
    commandId: assetsCommandIdSchema,
    expectedRevision: z.number().int().positive(),
    asset: assetsAssetInputSchema,
    reason: z.string().trim().min(10).max(500),
});
export const assetsAssetStateCommandSchema = z.strictObject({
    kind: z.enum(['archive', 'restore']),
    commandId: assetsCommandIdSchema,
    expectedRevision: z.number().int().positive(),
    reason: z.string().trim().min(10).max(500),
});
export const assetsAssetCommandSchema = z.discriminatedUnion('kind', [
    assetsUpdateAssetCommandSchema,
    assetsAssetStateCommandSchema,
]);
export const assetsAssetListQuerySchema = z.strictObject({
    query: z.string().trim().max(160).default(''),
    kind: assetsAssetKindSchema.nullable().default(null),
    status: assetsAssetStatusSchema.nullable().default(null),
    includeArchived: z.boolean().default(false),
    limit: z.number().int().min(1).max(500).default(100),
    offset: z.number().int().nonnegative().default(0),
});
const commandMetadataSchema = z.strictObject({
    commandId: assetsCommandIdSchema,
    idempotent: z.boolean(),
});
export const assetsAssetsResponseSchema = z.strictObject({
    data: z.array(assetsAssetSchema),
    page: z.strictObject({
        limit: z.number().int().positive(),
        offset: z.number().int().nonnegative(),
        returned: z.number().int().nonnegative(),
        total: z.number().int().nonnegative(),
    }),
});
export const assetsAssetResponseSchema = z.strictObject({
    data: assetsAssetSchema,
    command: commandMetadataSchema.optional(),
});
export const assetsAssetVersionsResponseSchema = z.strictObject({
    data: z.array(assetsAssetVersionSchema),
});
export function createAssetsFleetProfileDefaults(policy = {
    oilChangeIntervalMiles: 5_000,
    oilChangeWarningMiles: 750,
    oilChangeIntervalMonths: 3,
}) {
    return assetsFleetProfileSchema.parse({
        vin: null,
        licensePlate: null,
        year: null,
        color: null,
        fuelType: null,
        insuranceExpiry: null,
        registrationExpiry: null,
        nextServiceDate: null,
        lastOilChangeDate: null,
        lastOilChangeMileage: null,
        oilChangeIntervalMiles: policy.oilChangeIntervalMiles,
        oilChangeWarningMiles: policy.oilChangeWarningMiles,
        oilChangeIntervalMonths: policy.oilChangeIntervalMonths,
    });
}
export function createAssetsAssetDefaults(kind = 'other', fleetPolicy) {
    return {
        assetTag: '',
        name: '',
        kind,
        status: 'available',
        serialNumber: null,
        manufacturer: null,
        model: null,
        purchaseDate: null,
        locationLabel: null,
        notes: null,
        fleet: kind === 'van' || kind === 'trailer'
            ? createAssetsFleetProfileDefaults(fleetPolicy)
            : null,
    };
}
//# sourceMappingURL=assets-asset.contract.js.map